import { IndividualPlanTargetDialogStore } from './individual-plan-target-dialog.store';
import { fb } from '../../../../../modules/shared/shared.global-service';
import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { Validators } from '@angular/forms';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import {
    IndividualPlanTargetStatusEnum,
    individualPlanTargetStatusEnumOptions,
} from '@libs/types/src/services/enum/folder/individual-plan-target-status.enum';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import { changeSignal } from '../../../../../utils/form.utils';
import {
    FolderIndividualPlanTargetObject,
} from '@libs/types/src/services/object-type/folder/folder-individual-plan-target.object';
import { dateify, dateifyOrNow } from '../../../../../modules/shared/helpers/date.helper';
import { dateFromBeforeDateTo } from '../../../../../utils/date.form-validator';
import { TranslatePipe } from '@ngx-translate/core';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import {
    InputTextAutocompleteComponent,
} from '../../../../../modules/shared/components/inputs/input-text-autocomplete/input-text-autocomplete.component';

@Component({
    selector: 'app-individual-plan-target-dialog',
    templateUrl: './individual-plan-target-dialog.component.html',
    styleUrl: './individual-plan-target-dialog.component.scss',
    standalone: true,
    providers: [IndividualPlanTargetDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        SelectInputComponent,
        InputTextAutocompleteComponent,
        TranslatePipe,
    ],
})
export class IndividualPlanTargetDialogComponent extends BaseDialogComponent {

    public readonly store = inject(IndividualPlanTargetDialogStore);
    protected readonly folderStore = inject(FolderGlobalStore);

    protected readonly translatePrefix = 'individualPlan.target.';

    protected form = fb.group({
        id: fb.control<number | null>(null),
        name: fb.control<string>('', Validators.required),
        status: fb.control<string>(IndividualPlanTargetStatusEnum.IN_PROGRESS, Validators.required),
        dateFrom: fb.control<Date>(new Date(), Validators.required),
        dateTo: fb.control<Date | null>(null),
    });

    protected statusChange = changeSignal(this.form.controls.status);
    protected nameChange = changeSignal(this.form.controls.name);

    constructor() {
        super();

        effect(() => {
            if (this.statusChange() === IndividualPlanTargetStatusEnum.IN_PROGRESS) {
                this.form.controls.dateTo.setValidators([dateFromBeforeDateTo(this.form)]);
            } else {
                this.form.controls.dateTo.setValidators([Validators.required, dateFromBeforeDateTo(this.form)]);
            }
            this.form.controls.dateTo.updateValueAndValidity();
        });

        effect(() => {
            const name = this.nameChange() || '';
            if (name?.length < 2) return;

            this.store.api.getIndividualPlanTargetNamesByFolder({
                search: name,
                id: String(this.folderStore.folderId() || 0),
            });
        });

        const target: FolderIndividualPlanTargetObject | null = this.dialogConfig.data.target;
        if (target) {
            this.form.patchValue({
                id: target.id,
                name: target.name,
                status: target.status,
                dateFrom: dateifyOrNow(target.dateFrom),
                dateTo: dateify(target.dateTo),
            });
        }
    }

    public override save(): void {
        if (isInvalid(this.form)) return;

        this.dialogRef.close(this.form.value);
    }

    protected readonly individualPlanTargetStatusEnumOptions = individualPlanTargetStatusEnumOptions;
}
